/*
 * Ordering helpers for the file-edit asset list.
 *
 * "Asset type" here is a *filter group* (Backgrounds, Banners, Characters, ...),
 * not the `asset_type` table. Its value lives on a parsed file at
 * `file.filter[<groupId>]` as a single filter-item id (the group is single-select).
 *
 * Kept free of store/Vue imports — hence the group name is a parameter, not a
 * `@/constants` import — so this stays a pure module runnable under bare node
 * for the self-check (see sortFilesByAssetType.check.mjs).
 */

/**
 * Depth-first search for the leaf filter group with the given name.
 * Filter groups nest through `children_filter`; only leaves carry `filter_items`.
 */
export function findFilterGroupByName(filterTree, groupName) {
    if (Array.isArray(filterTree) === false) return null;

    for (const group of filterTree) {
        if (group == null) continue;

        if (group.children_filter == null) {
            if (group.name === groupName) return group;
        }
        else {
            const found = findFilterGroupByName(group.children_filter, groupName);
            if (found != null) return found;
        }
    }

    return null;
}

/**
 * Sort files by their asset-type name (case-insensitive, unset last).
 * Returns a new array; the input is never mutated. Stable — files sharing an
 * asset type keep their original relative order.
 */
export function sortFilesByAssetType(files, assetTypeFilter) {
    if (assetTypeFilter == null) return [...files];

    // id -> lowercased name, built once rather than searched per comparison.
    const nameByItemId = new Map();
    (assetTypeFilter.filter_items || []).forEach((item) => {
        if (item != null) nameByItemId.set(item.id, String(item.name).toLowerCase());
    });

    // Single-select group, so anything non-scalar — or a stale id no longer present
    // in filter_items — counts as unset rather than throwing.
    const nameOf = (file) => {
        const value = file != null && file.filter != null
            ? file.filter[assetTypeFilter.id]
            : null;
        return typeof value === 'number' ? nameByItemId.get(value) : undefined;
    };

    // Array.prototype.sort is stable (ES2019+), so equal names keep backend order.
    return [...files].sort((fileA, fileB) => {
        const a = nameOf(fileA);
        const b = nameOf(fileB);

        if (a === b) return 0;
        if (a === undefined) return 1;
        if (b === undefined) return -1;
        return a < b ? -1 : 1;
    });
}
